import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useSidebar } from "../../hooks/useSidebar";
import { useFiltroPersistido } from "../../hooks/useFiltroPersistido";
import { inventarioApi } from "../../services/inventario.api";
import { propiedadesApi } from "../../services/propiedades.api";
import { getMsg, getModal } from "../../utils/antdStatic";
import { Inventory } from "../../interfaces/api.interfaces";
import { useAuth } from "../../context/AuthContext";
import { formatPropertyName } from "../../utils/propertyDisplay";
// Misma lista de categorías que /flujos: se usa para descartar una categoría
// guardada que esta versión ya no ofrece.
import { CATEGORIA_LABEL } from "../flujos/flujos.api";

export interface Producto {
  id: number;
  nombre: string;
  /** Categoría financiera (misma lista que /flujos). Permanente y opcional. */
  categoria?: string | null;
  esPack: boolean;
  unidadesPorPack: number;
  cantidadDePacks: number;
  cantidadDeUnidades: number;
  ubicacion: string;
  ubicacionTipo: string;
  ubicacionId: number;
  /**
   * URL firmada de la foto característica, ya en tamaño THUMB (round
   * 2026-08-06). El backend manda UNA cadena, no la galería: firmar cada foto
   * de cada fila inflaba la respuesta sin que nadie las mirara. La galería
   * completa se sigue pidiendo aparte al abrir el modal de fotos.
   */
  miniatura?: string | null;
  /**
   * La MISMA foto en tamaño completo, para el visor al hacer clic. El thumb
   * ampliado se ve borroso justo cuando alguien quiere mirar el detalle.
   */
  miniaturaFull?: string | null;
  /** Marca, para poder distinguir dos productos que se llaman igual. */
  marca?: string | null;
  /**
   * En cuántos lugares MÁS vive este mismo producto (round 2026-08-06).
   *
   * Sin este dato, dos filas que comparten ficha se ven exactamente igual que
   * dos productos distintos — y editar una parecería no haber tocado la otra,
   * cuando en realidad las tocó a todas.
   */
  otrasUbicaciones?: number;
  /** Dado de baja con `hideItem`: sale del listado salvo que se pidan los ocultos. */
  oculto?: boolean;
  ocultoRazon?: string | null;
  ocultoDetalle?: string | null;
  /**
   * Id de la FICHA del catálogo (round 2026-08-06, etapa 2). Se descartaba en
   * `mapProducto`, así que el frontend no tenía cómo saber qué filas son el
   * mismo producto — solo el conteo agregado `otrasUbicaciones`. Sin él no se
   * puede agrupar. Es nullable: hay existencias sin ficha.
   */
  productoId?: number | null;
}

/**
 * Cómo se agrupa la tabla. `null` = listado plano, una fila por existencia.
 *
 * Las dos preguntas que el listado plano no responde:
 *   producto  → «¿cuánto tengo de esto en total, y repartido dónde?»
 *   ubicacion → «¿qué hay exactamente en esta bodega?»
 */
export type ModoAgrupacion = "producto" | "ubicacion" | null;

/**
 * Cabecera de grupo. La arma el SERVIDOR, porque la paginación es por grupo:
 * agrupar solo lo que está en pantalla partiría un producto en dos cuando sus
 * ubicaciones caen a ambos lados del corte de página.
 */
export interface GrupoInventario {
  clave: string;
  tipo: "producto" | "ubicacion";
  etiqueta: string;
  productoId: number | null;
  ubicacionTipo: string | null;
  ubicacionId: number | null;
  marca: string | null;
  categoria: string | null;
  miniatura: string | null;
  /** Ids de existencia que cuelgan del grupo, en el orden en que se muestran. */
  existencias: number[];
  totalUnidades: number;
  totalPacks: number;
  /** Ubicaciones distintas (grupo por producto). */
  lugares: number;
  /** Filas que contiene. */
  items: number;
}

/**
 * Fila que dibuja la tabla: o una cabecera de grupo, o una existencia.
 *
 * La existencia NO cambia de forma — es la misma `Producto` de siempre, con los
 * mismos ids. Eso es deliberado: editar, mover, ocultar, borrar, unificar, la
 * línea de tiempo y las fotos operan sobre el id de la existencia, y hay siete
 * endpoints `/api/inventario/:id/...` que dependen de eso.
 */
export type FilaTabla = Producto & {
  /** Solo en las cabeceras. Las filas reales no lo traen. */
  esGrupo?: boolean;
  grupo?: GrupoInventario;
  /** Clave única de la fila en la tabla: `"g:..."` en las cabeceras, el id en las existencias. */
  filaKey: string | number;
  children?: FilaTabla[];
};

/**
 * El conjunto completo de filtros de la vista Productos, tal como viaja al
 * endpoint `POST /api/inventario/filter`. Tenerlo como UN tipo evita que la
 * llamada y el contador de "filtros activos" se desincronicen cuando se
 * agrega un filtro nuevo.
 */
export interface FiltrosInventario {
  filterName?: string;
  advancedFilter?: string;
  filterProperties: (number | string)[];
  filterBodegas: (number | string)[];
  filterBodeguitas: (number | string)[];
  filterCountries: (number | string)[];
  filterRegions: (number | string)[];
  filterComunas: (number | string)[];
  filterSectors: (number | string)[];
  /** Categoría financiera del producto. `SIN_CATEGORIA` = los que no tienen. */
  filterCategorias: string[];
  /** `"con"` = queda stock, `"sin"` = agotado, `undefined` = no filtra. */
  filterStock?: "con" | "sin";
}

/**
 * Centinela de la opción "Sin categoría".
 *
 * La categoría es opcional (NULL en la DB) y NULL no sirve como `value` de un
 * `<Select>` multiple. Debe coincidir con la constante homónima del backend
 * (`inventario.controller.js`).
 */
export const SIN_CATEGORIA = "__sin_categoria__";

/** ¿Hay al menos un filtro con contenido? */
export const hayFiltros = (f: FiltrosInventario): boolean =>
  !!f.filterName?.trim() ||
  !!f.advancedFilter?.trim() ||
  f.filterProperties.length > 0 ||
  f.filterBodegas.length > 0 ||
  f.filterBodeguitas.length > 0 ||
  f.filterCountries.length > 0 ||
  f.filterRegions.length > 0 ||
  f.filterComunas.length > 0 ||
  f.filterSectors.length > 0 ||
  f.filterCategorias.length > 0 ||
  !!f.filterStock;

/** Cuántos filtros hay puestos (cada valor de un multi-select cuenta 1). */
export const contarFiltros = (f: FiltrosInventario): number =>
  (f.filterName?.trim() ? 1 : 0) +
  (f.advancedFilter?.trim() ? 1 : 0) +
  f.filterProperties.length +
  f.filterBodegas.length +
  f.filterBodeguitas.length +
  f.filterCountries.length +
  f.filterRegions.length +
  f.filterComunas.length +
  f.filterSectors.length +
  f.filterCategorias.length +
  (f.filterStock ? 1 : 0);

interface Property {
  id: number;
  nombre: string;
  pais: string;
  region: string;
  comuna: string;
  sector: string;
}
export interface MovimientoParams {
  productId: number; // id del producto que se va a mover
  destinoProductId?: number;
  origen_tipo: string; // tipo de ubicación de origen (‘bodegas’, ‘propiedades’, …)
  origen_id: number; // id concreto en esa ubicación
  destino_tipo: string; // tipo de ubicación destino
  destino_id: number | undefined; // id concreto en el destino
  cantidad_de_unidades: number; // cuántas unidades (si es movimiento de unidades)
  cantidad_de_packs: number; // cuántos packs (si es movimiento de packs)
  es_pack: boolean;
  cargo_a: string;
  fecha: string; // YYYY-MM-DD
}

interface Ubicacion {
  id: string | number; // el value que vas a enviar al backend
  pais?: string;
  region?: string;
  comuna?: string;
  sector?: string;
}
type IdMap<T extends { id: number | string }> = Record<string | number, T>;
type UbicacionTipo = "propiedades" | "bodegas" | "bodeguitas";
const toIdMap = <T extends { id: number | string }>(arr: T[] = []): IdMap<T> =>
  Object.fromEntries(arr.map((x) => [x.id, x]));

/**
 * Orden de la tabla. Se guarda con el vocabulario de Ant Design
 * (`ascend`/`descend`) porque es lo que la tabla necesita para dibujar la
 * flechita; la traducción al de la API ocurre en un solo lugar, abajo.
 */
export interface OrdenTabla {
  columna: string | null;
  direccion: "ascend" | "descend" | null;
}

/**
 * Orden → parámetros de la API.
 *
 * El orden es SERVER-SIDE a propósito: la tabla está paginada en el servidor,
 * así que ordenar en el navegador ordenaría las 50 filas visibles y no el
 * inventario — y se vería correcto, que es lo peor. Quien ordena por precio
 * para encontrar lo más caro vería el máximo de su página y lo tomaría por el
 * máximo de todo.
 */
const ordenParaApi = (orden: OrdenTabla) =>
  orden.columna && orden.direccion
    ? {
        ordenarPor: orden.columna,
        ordenDireccion: orden.direccion === "descend" ? "desc" : "asc",
      }
    : {};

/**
 * Columnas por las que se puede ordenar. ESPEJO de `COLUMNAS_ORDENABLES` en
 * [backend/src/utils/inventarioOrden.js] — una clave que esté acá y no allá
 * ofrece una flecha que no ordena nada, y el silencio es peor que no tenerla.
 *
 * `ubicacion` sí está: el backend ordena por tipo + id, que es lo que existe en
 * la tabla (el nombre legible se arma en el navegador cruzando tres listas
 * geográficas, así que no hay por dónde ordenarlo en la base).
 *
 * Vive acá, junto al estado `orden`, porque es también la lista contra la que
 * se valida el orden RESTAURADO de la sesión anterior: si viviera en la página
 * habría que importarla en círculo.
 */
export const COLUMNAS_ORDENABLES = new Set([
  "id",
  "nombre",
  "categoria",
  "marca",
  "modelo",
  "sku",
  "material",
  "color",
  "forma",
  "diseno",
  "tamano",
  "esPack",
  "unidadesPorPack",
  "cantidadDePacks",
  "cantidadDeUnidades",
  "precioPorPack",
  "precioPorUnidad",
  "volumenValor",
  "pesoValor",
  "numeroBoleta",
  "numeroFactura",
  "ubicacion",
]);

/**
 * Columnas por las que se pueden ordenar los GRUPOS. Espejo de
 * `CLAVES_ORDEN_GRUPO` en `backend/src/services/inventarioAgrupado.js`.
 *
 * Es más corta que la de arriba y tiene que serlo: agrupado, la columna "Total
 * de unidades" muestra la SUMA del grupo, así que ordenar por la columna real
 * de una fila daría un orden que no corresponde a lo que se ve. Precio, boleta,
 * SKU y formato directamente no significan nada en una cabecera.
 */
export const COLUMNAS_ORDENABLES_GRUPO = new Set([
  "nombre",
  "ubicacion",
  "categoria",
  "marca",
  "cantidadDeUnidades",
  "cantidadDePacks",
]);

/**
 * ¿El orden guardado sigue siendo un orden que esta versión sabe pedir?
 *
 * Se valida el PAR (columna + modo de agrupación), no cada valor por separado:
 * `precioPorUnidad` es una columna ordenable perfectamente válida en el listado
 * plano y no significa nada agrupado, donde el backend la ignoraría y la tabla
 * no dibujaría ninguna flecha — orden pedido y orden mostrado en desacuerdo.
 */
const esOrdenValido = (valor: unknown, agrupado: boolean): boolean => {
  if (!valor || typeof valor !== "object") return false;
  const { columna, direccion } = valor as OrdenTabla;
  if (columna === null && direccion === null) return true;
  if (typeof columna !== "string") return false;
  if (direccion !== "ascend" && direccion !== "descend") return false;
  return (agrupado ? COLUMNAS_ORDENABLES_GRUPO : COLUMNAS_ORDENABLES).has(columna);
};

/** Modos de agrupación que esta versión sabe pedir. */
const esAgrupacionValida = (valor: unknown): boolean =>
  valor === null || valor === "producto" || valor === "ubicacion";

/** Toda lista de ids de ubicación/geografía guardada tiene esta forma. */
const esListaDeIds = (valor: unknown): boolean =>
  Array.isArray(valor) &&
  valor.every((v) => typeof v === "number" || typeof v === "string");

/** Texto libre de un buscador: string, o `undefined` si nunca se escribió. */
const esTextoOpcional = (valor: unknown): boolean =>
  valor === undefined || typeof valor === "string";

export const useInventarioLogic = () => {
  const { sidebarOpen, toggleSidebar, setSidebarOpen } = useSidebar();
  const [ubicacionesReady, setUbicacionesReady] = useState(false);
  const [inventario, setInventario] = useState<Producto[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState({
    countries: false,
    regions: false,
    comunas: false,
    sectors: false,
    properties: false,
    bodegas: false,
    bodeguitas: false,
  });

  const [suggestions, setSuggestions] = useState<Record<string, string[]>>({});
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [total, setTotal] = useState(0);
  /**
   * Modo de agrupación, recordado entre sesiones.
   *
   * Arranca en `null` (listado plano) a propósito: es lo que el módulo hace
   * desde siempre, y cambiar de golpe lo que alguien ve al entrar es peor que
   * pedirle un clic. Una vez elegido, se queda.
   *
   * Se declara ANTES que `orden` porque el orden guardado se valida contra él:
   * agrupado hay menos columnas ordenables que en el listado plano.
   */
  const [agruparPor, setAgruparPorState] = useFiltroPersistido<ModoAgrupacion>(
    "inventario.agruparPor",
    null,
    { validar: esAgrupacionValida },
  );
  // Sin orden elegido, el backend usa su default (alfabético por nombre).
  const [orden, setOrden] = useFiltroPersistido<OrdenTabla>(
    "inventario.orden",
    { columna: null, direccion: null },
    { validar: (v) => esOrdenValido(v, agruparPor !== null) },
  );
  /** Cabeceras de grupo de la página actual. Vacío en modo plano. */
  const [grupos, setGrupos] = useState<GrupoInventario[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [bodegas, setBodegas] = useState<Property[]>([]);
  const [bodeguitas, setBodeguitas] = useState<Property[]>([]);
  const [propertiesRaw, setPropertiesRaw] = useState<
    Array<{ id: number; nombre: string; sectorId: number | null }>
  >([]);
  const [bodegasRaw, setBodegasRaw] = useState<
    Array<{ id: number; nombre: string; sectorId: number | null }>
  >([]);
  const [bodeguitasRaw, setBodeguitasRaw] = useState<
    Array<{ id: number; nombre: string; propiedadId: number }>
  >([]);
  const [countries, setCountries] = useState<Ubicacion[]>([]);
  const [comunas, setComunas] = useState<Ubicacion[]>([]);
  const [regions, setRegions] = useState<Ubicacion[]>([]);
  const [sectors, setSectors] = useState<Ubicacion[]>([]);
  const [countriesRaw, setCountriesRaw] = useState<any[]>([]);
  const [regionsRaw, setRegionsRaw] = useState<any[]>([]);
  const [comunasRaw, setComunasRaw] = useState<any[]>([]);
  const [sectorsRaw, setSectorsRaw] = useState<any[]>([]);
  const [similarProducts, setSimilarProducts] = useState<Inventory[]>([]);
  const [moveParams, setMoveParams] = useState<MovimientoParams>({
    productId: 0,
    origen_tipo: "",
    origen_id: 0,
    destino_tipo: "",
    destino_id: undefined,
    cantidad_de_unidades: 0,
    cantidad_de_packs: 0,
    es_pack: true,
    cargo_a: "",
    fecha: "",
  });
  const [resolvingLocations, setResolvingLocations] = useState(true);
  // inventario listo para UI (no muta el inventario "crudo")
  const [inventarioUI, setInventarioUI] = useState<Producto[]>([]);
  // Los filtros se recuerdan por usuario (round 2026-09-24): el admin que
  // filtra, se va a otra página y vuelve no tiene que ponerlos de nuevo.
  // Las listas guardan IDS, así que solo se valida la FORMA: un id que ya no
  // existe lo descarta el backend (respuesta vacía), no este validador.
  const [filterName, setFilterName] = useFiltroPersistido<string | undefined>(
    "inventario.nombre",
    undefined,
    { validar: esTextoOpcional },
  );
  const [filterSectors, setFilterSectors] = useFiltroPersistido<(number | string)[]>(
    "inventario.sectores",
    [],
    { validar: esListaDeIds },
  );
  const [filterComunas, setFilterComunas] = useFiltroPersistido<(number | string)[]>(
    "inventario.comunas",
    [],
    { validar: esListaDeIds },
  );
  const [filterRegions, setFilterRegions] = useFiltroPersistido<(number | string)[]>(
    "inventario.regiones",
    [],
    { validar: esListaDeIds },
  );
  const [filterCountries, setFilterCountries] = useFiltroPersistido<
    (number | string)[]
  >("inventario.paises", [], { validar: esListaDeIds });
  const [filterProperties, setFilterProperties] = useFiltroPersistido<
    (number | string)[]
  >("inventario.propiedades", [], { validar: esListaDeIds });
  const [filterBodegas, setFilterBodegas] = useFiltroPersistido<(number | string)[]>(
    "inventario.bodegas",
    [],
    { validar: esListaDeIds },
  );
  const [filterBodeguitas, setFilterBodeguitas] = useFiltroPersistido<
    (number | string)[]
  >("inventario.bodeguitas", [], { validar: esListaDeIds });
  const [advancedFilter, setAdvancedFilter] = useFiltroPersistido<string | undefined>(
    "inventario.busquedaLibre",
    undefined,
    { validar: esTextoOpcional },
  );
  const [filterCategorias, setFilterCategorias] = useFiltroPersistido<string[]>(
    "inventario.categorias",
    [],
    {
      validar: (v) =>
        Array.isArray(v) &&
        v.every(
          (c) => c === SIN_CATEGORIA || (typeof c === "string" && c in CATEGORIA_LABEL),
        ),
    },
  );
  const [filterStock, setFilterStock] = useFiltroPersistido<"con" | "sin" | undefined>(
    "inventario.stock",
    undefined,
    { validar: (v) => v === undefined || v === "con" || v === "sin" },
  );
  // "Mostrar ocultos": los productos dados de baja no salen en el listado,
  // y con ellos desaparecía su botón de eliminar. Ver `unhide`.
  const [incluirOcultos, setIncluirOcultos] = useFiltroPersistido<boolean>(
    "inventario.incluirOcultos",
    false,
    { validar: (v) => typeof v === "boolean" },
  );

  const { user } = useAuth();
  const propsById = useMemo(() => toIdMap(properties), [properties]);
  const bodegasById = useMemo(() => toIdMap(bodegas), [bodegas]);
  const bbById = useMemo(() => toIdMap(bodeguitas), [bodeguitas]);

  /**
   * Nombre de la PROPIEDAD dueña de cada bodeguita.
   *
   * `bodeguitas` (la lista compuesta) pierde el `propiedadId`, así que el
   * vínculo se reconstruye desde los datos crudos.
   */
  const bodeguitaPadreById = useMemo(() => {
    const nombrePorPropiedad = new Map(propertiesRaw.map((p) => [p.id, p.nombre]));
    const out: Record<number, string> = {};
    for (const b of bodeguitasRaw) {
      const n = nombrePorPropiedad.get(b.propiedadId);
      if (n) out[b.id] = n;
    }
    return out;
  }, [bodeguitasRaw, propertiesRaw]);

  const formatUbicacion = useCallback(
    (i: Producto): string => {
      // Las listas compuestas YA traen la geografía resuelta como NOMBRE (ver
      // el efecto que arma properties/bodegas/bodeguitas desde los sectores),
      // no como id. El código anterior hacía `comunasById[p.comuna]`, es decir
      // buscaba por id usando un nombre: siempre daba undefined y la columna
      // Ubicación terminaba mostrando SOLO el nombre, sin nada de geografía.
      // "Desconocido/a" es el relleno de ese mismo efecto cuando la ubicación
      // no tiene sector asignado; no aporta nada al usuario, así que se omite.
      const geo = (x: any): string[] =>
        [x.sector, x.comuna, x.region, x.pais].filter(
          (v: string) => v && !/^Desconocid[ao]$/i.test(v),
        );

      if (i.ubicacionTipo === "propiedades") {
        const p = propsById[i.ubicacionId];
        if (!p) return "Propiedad desconocida";
        return [formatPropertyName(p.nombre), ...geo(p)].join(" · ");
      }
      if (i.ubicacionTipo === "bodegas") {
        const b = bodegasById[i.ubicacionId];
        if (!b) return "Bodega desconocida";
        return [b.nombre, ...geo(b)].join(" · ");
      }
      if (i.ubicacionTipo === "bodeguitas") {
        const bb = bbById[i.ubicacionId];
        if (!bb) return "Bodeguita desconocida";
        // El "padre" de una bodeguita es su PROPIEDAD. Antes se resolvía con
        // `bodegasById[bb.id]`: indexaba el mapa de BODEGAS con el id de una
        // BODEGUITA. Son tablas distintas con secuencias de PK independientes,
        // así que lo que salía era una bodega ajena que casualmente tenía el
        // mismo número — un nombre EQUIVOCADO, no un dato faltante.
        const padre = bodeguitaPadreById[i.ubicacionId];
        return [bb.nombre, padre ? formatPropertyName(padre) : "", ...geo(bb)]
          .filter(Boolean)
          .join(" · ");
      }
      return "—";
    },
    [propsById, bodegasById, bbById, bodeguitaPadreById],
  );

  useEffect(() => {
    const listsReady =
      loaded.countries &&
      loaded.regions &&
      loaded.comunas &&
      loaded.sectors &&
      loaded.properties &&
      loaded.bodegas &&
      loaded.bodeguitas;
    if (listsReady) setUbicacionesReady(true);
    if (!inventario?.length) {
      setInventarioUI([]);
      setResolvingLocations(false);
      return;
    }
    if (!listsReady) {
      setResolvingLocations(true);
      // opcional: mostrar placeholders mientras cargan listas
      setInventarioUI(inventario.map((i) => ({ ...i, ubicacion: "" })));
      return;
    }

    setResolvingLocations(true);
    const hydrated = inventario.map((i) => ({
      ...i,
      ubicacion: formatUbicacion(i),
    }));
    setInventarioUI(hydrated);
    setResolvingLocations(false);
  }, [
    inventario,
    countries,
    regions,
    comunas,
    sectors,
    properties,
    bodegas,
    bodeguitas,
    formatUbicacion,
  ]);

  useEffect(() => {
    (async () => {
      await Promise.all([
        fetchCountries(),
        fetchRegions(),
        fetchComunas(),
        fetchSectors(),
      ]);
      await Promise.all([fetchProperties(), fetchBodegas(), fetchBodeguitas()]);
    })();
  }, []);

  useEffect(() => {
    // si aún no hay sectores, igual componemos y se actualizará cuando lleguen
    const sectorsById = new Map(sectors.map((s) => [s.id, s]));

    // PROPERTIES: sector vía sectorId
    const propertiesOut: Property[] = propertiesRaw.map((p) => {
      const sec = p.sectorId != null ? sectorsById.get(p.sectorId) : undefined;
      return {
        id: p.id,
        nombre: p.nombre,
        sector: sec?.sector ?? "Desconocido",
        comuna: sec?.comuna ?? "Desconocida",
        region: sec?.region ?? "Desconocida",
        pais: sec?.pais ?? "Desconocido",
      };
    });
    setProperties(propertiesOut);

    // BODEGAS: igual que properties
    const bodegasOut: Property[] = bodegasRaw.map((b) => {
      const sec = b.sectorId != null ? sectorsById.get(b.sectorId) : undefined;
      return {
        id: b.id,
        nombre: b.nombre,
        sector: sec?.sector ?? "Desconocido",
        comuna: sec?.comuna ?? "Desconocida",
        region: sec?.region ?? "Desconocida",
        pais: sec?.pais ?? "Desconocido",
      };
    });
    setBodegas(bodegasOut);

    // BODEGUITAS: sector se toma desde la propiedad a la que pertenecen
    const propertyById = new Map(propertiesOut.map((pp) => [pp.id, pp]));
    const bodeguitasOut: Property[] = bodeguitasRaw.map((bg) => {
      const prop = propertyById.get(bg.propiedadId);
      return {
        id: bg.id,
        nombre: bg.nombre,
        sector: prop?.sector ?? "Desconocido",
        comuna: prop?.comuna ?? "Desconocida",
        region: prop?.region ?? "Desconocida",
        pais: prop?.pais ?? "Desconocido",
      };
    });
    setBodeguitas(bodeguitasOut);
    setUbicacionesReady(true);
  }, [sectors, propertiesRaw, bodegasRaw, bodeguitasRaw]);

  const fetchCountries = async () => {
    const j = await propiedadesApi.getCountries();
    const raw = j?.data ?? [];
    setCountriesRaw(raw.map((p) => ({ id: p.id, nombre: p.pais })));
    setLoaded((s) => ({ ...s, countries: true }));
  };

  const fetchRegions = async () => {
    const j = await propiedadesApi.getRegions();
    const raw = j?.data ?? [];
    setRegionsRaw(
      raw.map((p: any) => ({
        id: p.id,
        nombre: p.region,
        paisId: p.paisId ?? p.pais_id,
      })),
    );
    setLoaded((s) => ({ ...s, regions: true }));
  };

  const fetchComunas = async () => {
    const j = await propiedadesApi.getComunas();
    const raw = j?.data ?? [];
    setComunasRaw(
      raw.map((p: any) => ({
        id: p.id,
        nombre: p.comuna,
        regionId: p.regionId ?? p.region_id,
      })),
    );
    setLoaded((s) => ({ ...s, comunas: true }));
  };

  const fetchSectors = async () => {
    const j = await propiedadesApi.getSectores();
    const raw = j?.data ?? [];
    setSectorsRaw(
      raw.map((p: any) => ({
        id: p.id,
        sector: p.sector,
        comunaId: p.comunaId ?? p.comuna_id,
      })),
    );
    setLoaded((s) => ({ ...s, sectors: true }));
  };

  // Componer nombres una vez carguen los RAW (un solo useEffect)
  useEffect(() => {
    const countryById = new Map(countriesRaw.map((c: any) => [c.id, c.nombre]));
    const regionsOut = regionsRaw.map((r: any) => ({
      id: r.id,
      region: r.nombre,
      pais: countryById.get(r.paisId) ?? "Desconocido",
    }));
    const regionById = new Map(regionsRaw.map((r: any) => [r.id, r]));
    const regionNameById = new Map(
      regionsRaw.map((r: any) => [r.id, r.nombre]),
    );

    const comunasOut = comunasRaw.map((c: any) => {
      const reg = regionById.get(c.regionId);
      return {
        id: c.id,
        comuna: c.nombre,
        region: regionNameById.get(c.regionId) ?? "Desconocida",
        pais: reg
          ? (countryById.get(reg.paisId) ?? "Desconocido")
          : "Desconocido",
      };
    });
    const comunaById = new Map(comunasRaw.map((c: any) => [c.id, c]));
    const regionsNameOutById = new Map(
      regionsOut.map((r: any) => [r.id, r.region]),
    );

    const sectorsOut = sectorsRaw.map((s: any) => {
      const com = comunaById.get(s.comunaId);
      const reg = com ? regionById.get(com.regionId) : undefined;
      return {
        id: s.id,
        sector: s.sector,
        comuna:
          comunasOut.find((x: any) => x.id === s.comunaId)?.comuna ??
          "Desconocida",
        region: reg
          ? (regionsNameOutById.get(reg.id) ?? "Desconocida")
          : "Desconocida",
        pais: reg
          ? (countryById.get(reg.paisId) ?? "Desconocido")
          : "Desconocido",
      };
    });

    setCountries(countriesRaw.map((c: any) => ({ id: c.id, pais: c.nombre })));
    setRegions(regionsOut);
    setComunas(comunasOut);
    setSectors(sectorsOut);
  }, [countriesRaw, regionsRaw, comunasRaw, sectorsRaw]);

  const fetchProperties = async () => {
    const json = await propiedadesApi.getAll();
    if (json) {
      const raw = Array.isArray(json.data) ? json.data : [];
      setPropertiesRaw(
        raw.map((p: any) => ({
          id: p.id,
          nombre:
            p.nombrepropiedad ??
            p.nombrePropiedad ??
            p.nombre ??
            `Propiedad ${p.id}`,
          sectorId: p.sectorId ?? p.sector_id ?? null,
        })),
      );
      setLoaded((s) => ({ ...s, properties: true }));
    }
  };

  const fetchBodegas = async () => {
    const json = await propiedadesApi.getBodegas();
    if (json) {
      const raw = Array.isArray(json.data) ? json.data : [];
      setBodegasRaw(
        raw.map((p: any) => ({
          id: p.id,
          nombre: (p as any).bodega ?? p.nombre ?? `Bodega ${p.id}`,
          sectorId: (p as any).sectorId ?? (p as any).sector_id ?? null,
        })),
      );
      setLoaded((s) => ({ ...s, bodegas: true }));
    }
  };

  const fetchBodeguitas = async () => {
    const json = await propiedadesApi.getBodeguitas();
    if (json) {
      const raw = Array.isArray(json.data) ? json.data : [];
      setBodeguitasRaw(
        raw.map((p: any) => ({
          id: p.id,
          nombre: (p as any).bodeguita ?? p.nombre ?? `Bodeguita ${p.id}`,
          propiedadId: p.id_propiedad ?? p.propiedadId ?? p.propiedad_id,
        })),
      );
      setLoaded((s) => ({ ...s, bodeguitas: true }));
    }
  };

  const resolveUbicacionNombre = useCallback(
    (tipo: string, id: number) => {
      const arr =
        tipo === "bodegas"
          ? bodegas
          : tipo === "bodeguitas"
            ? bodeguitas
            : properties;
      const found = arr.find((x) => x.id === id);
      return found?.nombre ?? `${tipo} #${id}`;
    },
    [properties, bodegas, bodeguitas],
  );

  const resolveSectorNombre = useCallback(
    (tipo: string, id: number) => {
      const arr =
        tipo === "bodegas"
          ? bodegas
          : tipo === "bodeguitas"
            ? bodeguitas
            : properties;
      const found: any = arr.find((x) => x.id === id);
      return found?.sector ?? "Sector desconocido";
    },
    [properties, bodegas, bodeguitas],
  );

  const normalizeUbicacion = (
    tipo: any,
    id: any,
  ): { tipo: UbicacionTipo; id: number } => {
    const t: UbicacionTipo =
      tipo === "bodegas" || tipo === "bodeguitas" || tipo === "propiedades"
        ? tipo
        : "propiedades";
    const i = typeof id === "number" && Number.isFinite(id) ? id : 0;
    return { tipo: t, id: i };
  };

  /**
   * Mapea una fila cruda del backend (snake_case) al `Producto` de la UI.
   *
   * Era el MISMO bloque de ~30 líneas copiado en `fetchInventario`,
   * `fetchFiltrado` y `limpiarFiltros`. Ya había divergido: dos copias
   * resolvían `ubicacion` al vuelo con `resolveUbicacionNombre` y la tercera
   * la dejaba en "" para que la hidratara el efecto — según por qué camino
   * llegaras, la columna Ubicación salía distinta. Ahora hay una sola copia y
   * la ubicación SIEMPRE la resuelve el efecto de hidratación.
   */
  const mapProducto = useCallback((p: any): Producto => {
    const u = normalizeUbicacion(p.ubicacion_tipo, p.ubicacion_id);
    return {
      id: p.id,
      nombre: p.nombre,
      categoria: p.categoria ?? null,

      marca: p.marca,
      modelo: p.modelo,
      sku: p.sku,
      material: p.material,
      color: p.color,
      forma: p.forma,
      diseno: p.diseno,
      tamano: p.tamano,
      volumenValor: p.volumen_valor,
      volumenUnidad: p.volumen_unidad,
      pesoValor: p.peso_valor,
      pesoUnidad: p.peso_unidad,

      esPack: !!p.es_pack,
      unidadesPorPack: p.unidades_por_pack ?? 0,
      cantidadDePacks: p.cantidad_de_packs ?? 0,
      cantidadDeUnidades: p.cantidad_de_unidades ?? 0,

      ubicacionTipo: u.tipo,
      ubicacionId: u.id,
      miniatura: p.miniatura ?? null,
      miniaturaFull: p.miniaturaFull ?? p.miniatura ?? null,
      otrasUbicaciones: Number(p.otrasUbicaciones) || 0,
      // Se descartaba, y sin él no hay forma de saber qué filas son el mismo
      // producto. `?? null` porque es nullable: existencias sin ficha.
      productoId: Number(p.producto_id) || null,
      oculto: !!p.oculto,
      ocultoRazon: p.oculto_razon ?? null,
      ocultoDetalle: p.oculto_detalle ?? null,

      // La resuelve el efecto de hidratación (inventarioUI), que espera a que
      // estén cargadas las listas de propiedades/bodegas/bodeguitas.
      ubicacion: "",

      precioPorPack: p.precio_por_pack,
      precioPorUnidad: p.precio_por_unidad,
      numeroBoleta: p.numero_boleta,
      numeroFactura: p.numero_factura,
    } as Producto;
  }, []);

  /**
   * Token de secuencia para descartar respuestas viejas.
   *
   * Sin esto, escribir rápido en el buscador (o apretar "Aplicar filtros" dos
   * veces) dispara varias requests y la tabla se queda con la que CONTESTA
   * última, no con la que se PIDIÓ última: buscabas "toalla" y quedabas
   * mirando el resultado de "toa". Cada fetch toma un número; solo el más
   * reciente puede escribir el estado.
   */
  const reqSeqRef = useRef(0);

  /** Aplica una respuesta solo si sigue siendo la request vigente. */
  const aplicarRespuesta = useCallback(
    (seq: number, json: any, mensajeError: string) => {
      if (seq !== reqSeqRef.current) return;
      if (json) {
        const raw: any[] = Array.isArray(json.data) ? json.data : [];
        setInventario(raw.map(mapProducto));
        // Las cabeceras vienen del servidor porque la paginación es POR GRUPO.
        // Si la respuesta no las trae (modo plano, o un backend viejo), se
        // limpian: dejar las anteriores dibujaría grupos que no corresponden a
        // estas filas.
        setGrupos(Array.isArray(json.grupos) ? json.grupos : []);
        // Sin esto el paginador quedaba con el total de la carga anterior
        // (número de páginas incorrecto) — auditoría 2026-07-13.
        setTotal(json.total ?? 0);
        setError(null);
      } else {
        setError(mensajeError);
      }
      setLoading(false);
    },
    [mapProducto],
  );

  const fetchInventario = useCallback(async () => {
    const seq = ++reqSeqRef.current;
    setLoading(true);
    setError(null);
    const json = await inventarioApi.getAll({
      page,
      size,
      incluirOcultos,
      ...ordenParaApi(orden),
      ...(agruparPor ? { agruparPor } : {}),
    } as any);
    aplicarRespuesta(seq, json, "Error al cargar inventario");
  }, [page, size, incluirOcultos, orden, agruparPor, aplicarRespuesta]);

  /**
   * Cambia el orden y vuelve a la página 1.
   *
   * Lo segundo no es un detalle: la página 3 del orden viejo no tiene ninguna
   * relación con la página 3 del nuevo, así que quedarse ahí deja al admin
   * mirando un tramo arbitrario de la lista y creyendo que ordenó.
   *
   * Pero volver a la página 1 SOLO si el orden cambió de verdad. Llamar a esta
   * función con el orden que ya estaba vigente —cosa que pasa cada vez que AntD
   * reporta un evento de tabla— tiene que ser inofensivo: si no, un clic de
   * paginación arrastra un `setPage(1)` que anula el cambio de página. El
   * llamador ya filtra por `extra.action`; esto es la segunda línea de defensa,
   * y además evita un objeto `orden` nuevo que dispararía un refetch de más.
   */
  const cambiarOrden = (
    columna: string | null,
    direccion: "ascend" | "descend" | null,
  ) => {
    const columnaNueva = direccion ? columna : null;
    if (orden.columna === columnaNueva && orden.direccion === direccion) return;
    setOrden({ columna: columnaNueva, direccion });
    setPage(1);
  };

  /**
   * Cambia el modo de agrupación y vuelve a la página 1.
   *
   * Lo segundo es obligatorio, no cosmético: al agrupar, una página deja de
   * contener existencias y pasa a contener GRUPOS. La página 4 de un listado de
   * 100 existencias no existe en un listado de 15 productos, y el paginador
   * pediría un tramo vacío.
   */
  const cambiarAgrupacion = (modo: ModoAgrupacion) => {
    if (modo === agruparPor) return;
    setAgruparPorState(modo);
    setPage(1);
  };

  /**
   * Las filas que dibuja la tabla: plano o árbol, según el modo.
   *
   * El árbol se arma acá y no en el servidor porque el NOMBRE de la ubicación
   * se resuelve en el frontend (`formatUbicacion` cruza propiedades, bodegas,
   * bodeguitas y geografía). El servidor manda el reparto y el orden — que es
   * lo que no se puede decidir mirando una página— y acá se le pone el nombre.
   *
   * Las filas hijas son las MISMAS de siempre, con el mismo `id`: eso es lo que
   * permite que editar, mover, ocultar, borrar y la línea de tiempo sigan
   * funcionando sin tocar una línea.
   */
  const filasTabla = useMemo<FilaTabla[]>(() => {
    if (!agruparPor || !grupos.length) {
      return inventarioUI.map((p) => ({ ...p, filaKey: p.id }));
    }

    const porId = new Map(inventarioUI.map((p) => [p.id, p]));
    const usados = new Set<number>();

    const arbol: FilaTabla[] = grupos.map((g) => {
      const hijos: FilaTabla[] = [];
      for (const id of g.existencias || []) {
        const fila = porId.get(id);
        if (!fila) continue;
        usados.add(id);
        hijos.push({ ...fila, filaKey: fila.id });
      }

      return {
        // La cabecera se hace pasar por una fila para que las columnas la
        // dibujen sin casos especiales: los agregados van donde la columna
        // espera el valor de una existencia.
        id: 0,
        nombre: g.etiqueta,
        marca: g.tipo === "producto" ? g.marca : null,
        categoria: g.categoria,
        miniatura: g.miniatura,
        esPack: false,
        unidadesPorPack: 0,
        cantidadDePacks: g.totalPacks,
        cantidadDeUnidades: g.totalUnidades,
        // Una cabecera de producto no tiene una ubicación: tiene varias. La
        // celda lo dice con el conteo en vez de inventar un lugar.
        ubicacion: g.tipo === "ubicacion" ? g.etiqueta : "",
        ubicacionTipo: g.ubicacionTipo ?? "",
        ubicacionId: g.ubicacionId ?? 0,
        productoId: g.productoId,
        otrasUbicaciones: 0,
        esGrupo: true,
        grupo: g,
        filaKey: `g:${g.clave}`,
        children: hijos,
      } as FilaTabla;
    });

    // Red de seguridad: una fila que el servidor mandó pero que no quedó dentro
    // de ningún grupo NO puede desaparecer de la tabla. Que se vea suelta es
    // feo; que se pierda stock de la vista sin aviso es un bug de los que no se
    // detectan mirando.
    const huerfanas = inventarioUI
      .filter((p) => !usados.has(p.id))
      .map((p) => ({ ...p, filaKey: p.id }) as FilaTabla);

    return [...arbol, ...huerfanas];
  }, [agruparPor, grupos, inventarioUI]);

  // ¿Hay filtros server-side aplicados? Lo setea "Aplicar filtros" y lo
  // limpia "Limpiar". Es un ref (no state) para no re-disparar el efecto.
  const filtrosActivosRef = useRef(false);

  /**
   * Recarga la página actual RESPETANDO los filtros vigentes.
   *
   * Todas las mutaciones (crear, editar, borrar, mover, ocultar) llamaban a
   * `fetchInventario`, que es el listado SIN filtrar: con filtros puestos,
   * editar un producto hacía que la tabla saltara al inventario completo
   * mientras el panel seguía mostrando "Filtros activos (3)". Peor, el ref
   * quedaba en `true`, así que la siguiente paginación volvía al set
   * filtrado y la lista cambiaba sola otra vez.
   */
  const refetchRef = useRef<() => void>(() => {});

  /** ¿Ya se hizo la primera carga del listado en este montaje? */
  const primeraCargaRef = useRef(false);

  useEffect(() => {
    if (!ubicacionesReady) return;
    // Filtros RESTAURADOS de la sesión anterior (round 2026-09-24): el estado
    // no alcanza. Quien decide "¿pido el set filtrado o el listado completo?"
    // es este ref, que solo lo tocaba "Aplicar filtros"; sin esto la primera
    // carga traía el inventario ENTERO mientras el panel decía "Filtros
    // activos (5)" — el mismo desacuerdo que documenta el comentario de
    // arriba, esta vez desde el arranque.
    if (!primeraCargaRef.current) {
      primeraCargaRef.current = true;
      filtrosActivosRef.current = hayFiltros(filtrosActuales());
    }
    // Con filtros activos, el cambio de página pide la página FILTRADA.
    // Antes siempre llamaba fetchInventario (getAll sin filtros): paginar
    // resultados filtrados descartaba la búsqueda y mostraba el inventario
    // completo (auditoría 2026-07-13).
    if (filtrosActivosRef.current) {
      void fetchFiltrado(page);
    } else {
      void fetchInventario();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ubicacionesReady, fetchInventario]);

  const fetchSuggestions = useCallback(async () => {
    const res = await inventarioApi.getDistinctAll();
    if (res?.data) setSuggestions(res.data);
  }, []);

  useEffect(() => {
    fetchSuggestions();
  }, [fetchSuggestions]);

  /**
   * Crea la compra y devuelve la RESPUESTA COMPLETA, no solo el id.
   *
   * Round 2026-08-20: el backend distingue tres desenlaces —producto nuevo,
   * stock sumado a una fila existente, existencia nueva de un producto que ya
   * existía en otro lugar— y el caller necesita saber cuál fue para explicarlo.
   * Devolviendo el id pelado, los tres se anunciaban igual ("Producto creado
   * correctamente"), que además es falso en dos de ellos.
   */
  const onCreate = async (newProduct: any): Promise<any | null> => {
    const res = await inventarioApi.create(newProduct);
    if (res) { refetchRef.current(); fetchSuggestions(); }
    return res ?? null;
  };

  const onEdit = async (prod: Producto) => {
    const res = await inventarioApi.update(prod.id, prod as any);
    if (!res) return;
    refetchRef.current();
    fetchSuggestions();

    // Round 2026-08-05 — corregir la FECHA DE COMPRA no es un cambio más del
    // producto: mueve el egreso (y el cobro al cliente / reembolso vinculados)
    // al mes que indique el admin. El backend lo devuelve en `correccionFecha`;
    // acá se avisa aparte del toast genérico. Si el cambio toca un mes YA
    // CERRADO para el cliente, el aviso es un modal persistente — un toast de
    // 3 s es fácil de perderse y esos números ya los vio el cliente.
    // El backend descarta cualquier precio que venga en una edición: es el
    // promedio ponderado de las compras, no un campo del formulario. Con la UI
    // actual no debería llegar ninguno —los campos son de solo lectura al
    // editar— así que si esto aparece, algo lo está mandando igual y conviene
    // verlo en vez de que se descarte sin rastro.
    const ignorado = (res as any)?.precioIgnorado;
    if (ignorado?.length) {
      getMsg()?.warning(
        "El precio no se cambia editando el producto: se recalcula como el promedio ponderado de sus compras. Corrígelo en la pestaña Finanzas, editando la transacción.",
      );
    }

    const corr = (res as any)?.correccionFecha;
    if (!corr) return;
    const detalle = `Fecha de la compra: ${corr.anterior ?? "—"} → ${corr.nueva}.`;
    if (corr.mesesCerrados?.length) {
      getModal()?.warning({
        title: "La compra cambió de mes y afecta un cierre publicado",
        content: `${detalle} ${corr.mesesCerrados.join(" y ")} ya está cerrado para el cliente: los números que vio cambian de forma retroactiva.`,
      });
    } else {
      getMsg()?.info(detalle);
    }
  };

  /**
   * Borra un producto y TODO su registro financiero. Devuelve `true` solo si
   * el DELETE fue exitoso — `apiFetch` no lanza en error (devuelve null), así
   * que el caller NO puede usar try/catch para detectar la falla.
   */
  const onDelete = async (prod: Producto): Promise<boolean> => {
    const ok = await inventarioApi.delete(prod.id);
    if (ok !== null) refetchRef.current();
    return ok !== null;
  };

  /**
   * Borrado masivo: dispara N DELETE en paralelo (`deleteSilent` para
   * evitar N toasts apilados) y refetchea UNA sola vez al final.
   * Devuelve `{ ok, fail }` para que el caller muestre un único mensaje
   * agregado. Igual que `onDelete`, esto es destructivo: borra el
   * producto y TODO su registro financiero asociado (compras,
   * movimientos), no es ocultar.
   */
  const onBulkDelete = async (
    ids: number[],
  ): Promise<{ ok: number; fail: number }> => {
    if (!ids.length) return { ok: 0, fail: 0 };
    const results = await Promise.all(
      ids.map((id) => inventarioApi.deleteSilent(id)),
    );
    const ok = results.filter((r) => r !== null).length;
    const fail = results.length - ok;
    refetchRef.current();
    return { ok, fail };
  };

  const onMove = async (movimiento: MovimientoParams) => {
    const ok = await inventarioApi.move({
      movimiento,
      quien_realiza: user,
    } as any);
    if (ok) refetchRef.current();
  };

  /** Los filtros tal como están AHORA en el estado. */
  const filtrosActuales = useCallback(
    (): FiltrosInventario => ({
      filterName,
      advancedFilter,
      filterProperties,
      filterBodegas,
      filterBodeguitas,
      filterCountries,
      filterRegions,
      filterComunas,
      filterSectors,
      filterCategorias,
      filterStock,
    }),
    [
      filterName,
      advancedFilter,
      filterProperties,
      filterBodegas,
      filterBodeguitas,
      filterCountries,
      filterRegions,
      filterComunas,
      filterSectors,
      filterCategorias,
      filterStock,
    ],
  );

  // Fetch de una página del set FILTRADO (server-side). Lo usan tanto
  // "Aplicar filtros" (página 1) como el cambio de página con filtros
  // activos (ver el efecto de paginación).
  //
  // `overrides` existe por los chips de "quitar este filtro": ahí hay que
  // consultar con un valor que TODAVÍA no está en el estado (React agenda el
  // set y re-renderiza después), así que se pasa explícito.
  const fetchFiltrado = async (
    pageArg: number,
    overrides?: Partial<FiltrosInventario>,
  ) => {
    const f = { ...filtrosActuales(), ...overrides };
    const seq = ++reqSeqRef.current;
    setLoading(true);
    setError(null);
    const json = await inventarioApi.filter({
      page: pageArg,
      size,
      incluirOcultos,
      ...ordenParaApi(orden),
      ...(agruparPor ? { agruparPor } : {}),
      ...f,
    });
    aplicarRespuesta(seq, json, "Error al aplicar los filtros");
  };

  const aplicarFiltros = async (overrides?: Partial<FiltrosInventario>) => {
    const f = { ...filtrosActuales(), ...overrides };
    // Con TODO vacío no hay por qué pagar el endpoint de filtro (que hace
    // JOIN con propiedades/bodegas/bodeguitas): se vuelve al listado base.
    filtrosActivosRef.current = hayFiltros(f);

    // Aplicar filtros SIEMPRE vuelve a la página 1: si estabas en la
    // página 3 y el resultado filtrado tiene 1 sola página, quedabas
    // mirando una página vacía.
    if (page !== 1) {
      setPage(1); // el efecto de paginación dispara el fetch correspondiente
      return;
    }
    if (!filtrosActivosRef.current) {
      await fetchInventario();
      return;
    }
    await fetchFiltrado(1, overrides);
  };

  const limpiarFiltros = async () => {
    filtrosActivosRef.current = false;
    setFilterName(undefined);
    setAdvancedFilter(undefined);
    setFilterBodegas([]);
    setFilterBodeguitas([]);
    setFilterProperties([]);
    setFilterCountries([]);
    setFilterRegions([]);
    setFilterComunas([]);
    setFilterSectors([]);
    setFilterCategorias([]);
    setFilterStock(undefined);
    // "Mostrar ocultos" también se limpia: se recuerda entre sesiones, y si
    // "Limpiar" no lo apagara quedaría trayendo los productos dados de baja
    // para siempre (sumándolos al total y a las cabeceras de grupo).
    setIncluirOcultos(false);

    // Volver a la base (primera página). Si ya estábamos en la 1, `setPage(1)`
    // no cambia el estado y el efecto no vuelve a disparar, así que el fetch
    // se hace acá; si veníamos de otra página, el `setPage` es lo que manda y
    // el efecto trae la página 1 sin filtros.
    if (page !== 1) {
      setPage(1);
      return;
    }
    const seq = ++reqSeqRef.current;
    setLoading(true);
    setError(null);
    const json = await inventarioApi.getAll({
      page: 1,
      size,
      // Literal, no la variable: el `setIncluirOcultos` de arriba no se ve en
      // este closure y pediríamos otra vez el listado con las bajas adentro.
      incluirOcultos: false,
      ...(agruparPor ? { agruparPor } : {}),
    } as any);
    aplicarRespuesta(seq, json, "Error al cargar inventario");
  };

  /**
   * Quita UN filtro puntual (el "×" de cada chip) y vuelve a consultar.
   *
   * El valor nuevo se pasa como override porque `setState` no es síncrono:
   * el chip de búsqueda libre hacía `setAdvancedFilter(""); aplicarFiltros();`
   * y `aplicarFiltros` seguía leyendo el texto VIEJO del closure de ese
   * render — el chip desaparecía de la pantalla pero el filtro seguía
   * aplicado en el servidor.
   */
  const quitarFiltro = async (
    campo: keyof FiltrosInventario,
    valor?: number | string,
  ) => {
    const actual = filtrosActuales()[campo];
    let nuevo: any;
    if (Array.isArray(actual)) {
      nuevo = valor === undefined ? [] : actual.filter((v) => v !== valor);
    } else {
      nuevo = undefined;
    }

    const setters: Record<keyof FiltrosInventario, (v: any) => void> = {
      filterName: setFilterName,
      advancedFilter: setAdvancedFilter,
      filterProperties: setFilterProperties,
      filterBodegas: setFilterBodegas,
      filterBodeguitas: setFilterBodeguitas,
      filterCountries: setFilterCountries,
      filterRegions: setFilterRegions,
      filterComunas: setFilterComunas,
      filterSectors: setFilterSectors,
      filterCategorias: setFilterCategorias,
      filterStock: setFilterStock,
    };
    setters[campo](nuevo);
    await aplicarFiltros({ [campo]: nuevo } as Partial<FiltrosInventario>);
  };

  // El refetch que usan las mutaciones. Se reasigna en cada render (mismo
  // patrón de "latest ref" que ya usa `aplicarFiltrosRef` en la página) para
  // que siempre apunte a la versión con los filtros y la página vigentes.
  refetchRef.current = () => {
    if (filtrosActivosRef.current) void fetchFiltrado(page);
    else void fetchInventario();
  };

  /**
   * Cambia el tamaño de página volviendo SIEMPRE a la primera.
   *
   * `onChange={setSize}` a secas conservaba la página: estando en la 8 con 25
   * por página y pasando a 50, pedía la página 8 de 50 — más allá del final
   * del listado, o sea tabla vacía sin explicación.
   */
  const cambiarSize = (nuevo: number) => {
    setSize(nuevo);
    setPage(1);
  };

  const getImages = async (id: number) => {
    const result = await inventarioApi.getImages(id);
    return result?.data;
  };

  const uploadMediaFile = async (productId: number, file: File): Promise<string> => {
    const form = new FormData();
    form.append("file", file, file.name);

    const result = await inventarioApi.uploadImage(productId, form);
    if (!result) throw new Error("Error subiendo archivo");

    const url = result.data;
    if (!url || typeof url !== "string") {
      throw new Error(result.message || "Archivo subido pero no se obtuvo URL");
    }
    return url;
  };

  const deleteImage = async (id: number, url: string): Promise<string[]> => {
    const json = await inventarioApi.deleteImage(id, url);
    if (!json?.success)
      throw new Error(
        json?.error || json?.message || "Error al eliminar imagen",
      );
    return json.data?.urls ?? [];
  };

  // tipa el retorno: siempre un array
  const getSimilarProducts = async (
    movimiento: MovimientoParams,
  ): Promise<Inventory[]> => {
    try {
      const json = await inventarioApi.getSimilar({ movimiento });
      if (!json) throw new Error("Error al obtener productos similares");

      // Normaliza distintas formas posibles:
      // - backend devuelve array directo
      // - backend devuelve { data: [...] }
      const arr: any[] = Array.isArray(json)
        ? json
        : Array.isArray(json?.data)
          ? json.data
          : [];

      // Algunas libs devuelven filas como [obj, ...]; nos quedamos con el primero
      const rows = arr
        .map((r) => (Array.isArray(r) ? r[0] : r))
        .filter(Boolean);

      const mappedInventory: Inventory[] = rows.map((p: any) => ({
        id: p.id,
        nombre: p.nombre,
        marca: p.marca,
        modelo: p.modelo,
        sku: p.sku,
        material: p.material,
        color: p.color,
        forma: p.forma,
        diseno: p.diseno,
        tamano: p.tamano,
        volumenValor: p.volumen_valor,
        volumenUnidad: p.volumen_unidad,
        pesoValor: p.peso_valor,
        pesoUnidad: p.peso_unidad,
        esPack: p.es_pack,
        unidadesPorPack: p.unidades_por_pack,
        cantidadDePacks: p.cantidad_de_packs,
        cantidadDeUnidades: p.cantidad_de_unidades,
        ubicacionTipo: p.ubicacion_tipo,
        ubicacionId: p.ubicacion_id,
        ubicacion: resolveUbicacionNombre(p.ubicacion_tipo, p.ubicacion_id),
        sector: resolveSectorNombre(p.ubicacion_tipo, p.ubicacion_id),
        precioPorPack: p.precio_por_pack,
        precioPorUnidad: p.precio_por_unidad,
        numeroBoleta: p.numero_boleta,
        // OJO: tu código tenía p.numerofactura (sin guion bajo). Ajusto al snake_case típico:
        numeroFactura: p.numero_factura ?? p.numerofactura ?? null,
        // si el backend manda el score, lo conservamos; si no, queda undefined
        similarity_score:
          typeof p.similarity_score === "number"
            ? p.similarity_score
            : undefined,
      }));

      setSimilarProducts(mappedInventory); // si quieres mantener el estado aquí
      return mappedInventory; // ← SIEMPRE array
    } catch (error: any) {
      getMsg()?.error(error?.message ?? "Error al obtener productos similares");
      setSimilarProducts([]); // limpia estado si falló
      return []; // ← devuelve array vacío, no undefined
    }
  };

  const handleHideProduct = async (values: any, id: number | null) => {
    if (!id) return;
    const ok = await inventarioApi.hide(id, {
      razon: values.razon,
      detalle: values.detalle?.trim() || null,
    });
    if (ok) refetchRef.current();
  };

  /** Vuelve a mostrar un producto ocultado y refresca respetando los filtros. */
  const onUnhide = async (id: number): Promise<boolean> => {
    const ok = await inventarioApi.unhide(id);
    if (ok) refetchRef.current();
    return !!ok;
  };

  return {
    inventario: inventarioUI,
    loading,
    error,
    page,
    size,
    total,
    moveParams,
    countries,
    regions,
    sectors,
    comunas,
    properties,
    bodegas,
    bodeguitas,
    filterName,
    filterBodegas,
    filterBodeguitas,
    filterProperties,
    filterCountries,
    filterRegions,
    filterComunas,
    filterSectors,
    filterCategorias,
    filterStock,
    advancedFilter,
    similarProducts,
    sidebarOpen,
    resolvingLocations,
    ubicacionesReady,
    toggleSidebar,
    setSidebarOpen,
    setFilterBodeguitas,
    setFilterBodegas,
    setFilterProperties,
    setFilterComunas,
    setFilterName,
    setFilterCountries,
    setFilterRegions,
    setFilterSectors,
    setFilterCategorias,
    setFilterStock,
    setAdvancedFilter,
    setMoveParams,
    setSimilarProducts,
    setPage,
    setSize,
    cambiarSize,
    orden,
    cambiarOrden,
    agruparPor,
    cambiarAgrupacion,
    grupos,
    /** Filas de la tabla: planas o en árbol según `agruparPor`. */
    filasTabla,
    /**
     * Recarga la página actual RESPETANDO los filtros vigentes.
     *
     * Se expone para las mutaciones que viven en la página y no en el hook
     * (hoy: unificar fichas). Es la MISMA función que usan las demás
     * mutaciones — refrescar con `fetchInventario` a secas descartaría los
     * filtros y la tabla saltaría al inventario completo mientras el panel
     * sigue diciendo "Filtros activos (3)".
     */
    recargar: () => refetchRef.current(),
    incluirOcultos,
    setIncluirOcultos,
    onUnhide,
    quitarFiltro,
    filtros: filtrosActuales(),
    onCreate,
    onEdit,
    onDelete,
    onBulkDelete,
    user,
    aplicarFiltros,
    limpiarFiltros,
    onMove,
    getImages, // función para obtener URLs de imágenes
    uploadMediaFile,
    deleteImage,
    getSimilarProducts,
    handleHideProduct,
    suggestions,
  };
};

